import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toCanonicalRole } from '../../context/RoleMatrixContext';
import { getReminderViews } from '../../lib/reminderViews';

const changeEvent = 'reminders-view-change';
const normalize = (value, options) => options.find(option => option.value === value)?.value || options[0]?.value || '';
function readView(key, options) {
  try { return normalize(localStorage.getItem(key), options); }
  catch (_) { return normalize(null, options); }
}

// Keep the table and sidebar on the same view, including other tabs. Store no client data.
export default function useReminderView() {
  const { currentRole, currentUser } = useAuth();
  const role = toCanonicalRole(currentRole?.value || currentRole);
  const identityKey = `${currentUser?.userId || ''}:${currentUser?.staffProfileId || ''}:${role}`;
  const key = `path.reminders.view.v1:${identityKey}`;
  const options = getReminderViews(role);
  const [selection, setSelection] = useState(() => ({ key, value: readView(key, options) }));
  const view = selection.key === key ? normalize(selection.value, options) : readView(key, options);
  useEffect(() => {
    const currentOptions = getReminderViews(role);
    setSelection({ key, value: readView(key, currentOptions) });
    const onChange = event => {
      if (event.detail?.key === key) setSelection({ key, value: normalize(event.detail.value, currentOptions) });
    };
    const onStorage = event => {
      if (event.key === key || event.key === null) setSelection({ key, value: readView(key, currentOptions) });
    };
    window.addEventListener(changeEvent, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(changeEvent, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [key, role]);
  const setView = value => {
    const next = normalize(value, options);
    try { localStorage.setItem(key, next); } catch (_) { /* Retain the selection for this session. */ }
    setSelection({ key, value: next });
    window.dispatchEvent(new CustomEvent(changeEvent, { detail: { key, value: next } }));
  };
  return { view, setView, options, identityKey, ready: !currentUser?.loading };
}
